/**
 * AiAnalysisCardExtra：AI 分析卡片右上角操作区（extra）。
 *
 * 交互契约：
 * - 编辑态（isEdit=true）：仅提供“编辑/删除”等布局层操作，不触发分析相关行为；
 * - 预览态（isEdit=false）：提供“分析方案选择/编辑”入口，以及“过滤条件变化后刷新”的快捷操作。
 */
import React, { memo, useState } from 'react';
import classNames from 'classnames';
import { List } from 'tea-component';
import type { IntlShape } from 'react-intl';

import CardIconButton from '@/pages/datalab/components/ChartCardIconButtons/CardIconButton';
import OperationDropdownIcon from '@/pages/datalab/components/ChartCardIconButtons/OperationDropdownIcon';
import AiAnalysisApproachIcon from '@/pages/datalab/components/ChartCardIconButtons/AiAnalysisApproachIcon';
import FullScreenIcon from '@/pages/datalab/components/ChartCardIconButtons/FullscreenIcon';
import SvgIcon from '@home/components/SvgIcon';

import refreshIcon from '@/pages/aigc/assets/svg/refresh.svg';
import refreshActiveIcon from '@/pages/aigc/assets/svg/refresh-active.svg';

import type { IAiPromptInfo, IAiPromptOption } from '../types';

interface IAiAnalysisCardExtraProps {
  isEdit: boolean;
  promptOptions: IAiPromptOption[];
  promptId?: number;
  currentPromptInfo?: IAiPromptInfo;
  hasConversation: boolean;
  fullScreenContentRef: React.RefObject<HTMLElement | null>;
  formatMessage: IntlShape['formatMessage'];
  onEditApproach: (nextPromptId: string) => void;
  onSelectApproach: (nextPromptId: string) => void;
  onRefreshClick: (options?: { refresh?: boolean }) => void;
  onEditPresetValue: () => void;
  onDeleteCard: () => void;
  onFullScreenExit?: () => void;
}

const AiAnalysisCardExtra: React.FC<IAiAnalysisCardExtraProps> = (props) => {
  const {
    isEdit,
    promptOptions,
    promptId,
    currentPromptInfo,
    hasConversation,
    fullScreenContentRef,
    formatMessage,
    onEditApproach,
    onSelectApproach,
    onRefreshClick,
    onEditPresetValue,
    onDeleteCard,
    onFullScreenExit,
  } = props;

  const [hoverRefresh, setHoverRefresh] = useState<boolean>(false);
  const [isFullScreen, setIsFullScreen] = useState<boolean>(false);


  if (isEdit) {
    return (
      <OperationDropdownIcon>
        <List type="option">
          <List.Item onClick={onEditPresetValue}>
            <SvgIcon type="pencil-m" />
            {formatMessage({ id: 'dbl_common_edit' })}
          </List.Item>
          <List.Item onClick={onDeleteCard}>
            <SvgIcon type="trash-can-icon" />
            {formatMessage({ id: 'dbl_common_delete' })}
          </List.Item>
        </List>
      </OperationDropdownIcon>
    );
  }

  const popupContainer = isFullScreen ? (fullScreenContentRef.current || undefined) : undefined;

  return (
    <>
      <AiAnalysisApproachIcon
        options={promptOptions}
        activePromptId={String(promptId ?? '')}
        isCustomActive={Boolean(currentPromptInfo?.isCustom)}
        onEdit={onEditApproach}
        onSelect={onSelectApproach}
        bubbleProps={{
          popupContainer,
        }}
      />

      {hasConversation ? (
        <CardIconButton
          bubbleProps={{
            content: formatMessage({ id: 'dbl_common_refresh_text' }),
            popupContainer,
          }}
          onClick={() => {
            // refresh=true 表示用户显式要求“基于已有配置的显式刷新重跑”：
            // AiAnalysisCardView 的 handleRunAnalysis 会把它透传到后端请求体（refresh 字段），
            // 从而让后端走“刷新语义”，避免把普通的再次分析与显式刷新混为一类。
            onRefreshClick({ refresh: true });
          }}
          onMouseEnter={() => {
            setHoverRefresh(true);
          }}
          onMouseLeave={() => {
            setHoverRefresh(false);
          }}
        >
          <img
            src={hoverRefresh ? refreshActiveIcon : refreshIcon}
            className={classNames('dbl-ai-analysis-card__refresh-icon')}
            alt="refresh icon"
          />
        </CardIconButton>
      ) : null}

      <FullScreenIcon
        target={fullScreenContentRef}
        onToggle={(isFullScreen) => {
          setIsFullScreen(isFullScreen);
          if (!isFullScreen) {
            onFullScreenExit?.();
          }
        }}
      />

      {isFullScreen && (
        <CardIconButton
          onClick={() => {
            if (document.exitFullscreen) {
              document.exitFullscreen();
            }
          }}
          bubbleProps={{
            content: formatMessage({ id: 'dbl_common_close' }),
            popupContainer,
          }}
        >
          <SvgIcon type="clonse" />
        </CardIconButton>
      )}
    </>
  );
};

export default memo(AiAnalysisCardExtra);

